import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {BoardSeat, CompanyBasic, Position} from '../../models';
import { AuthorizationService, FinancialUtilsService, RmsUtilsService } from '../../../core/services';
import { PERMISSIONS } from '../../enums/permissions.enum';
import { KeyInflectionDataService } from '../../../modules/milestones/services/key-inflection-data.service';
import { FinancialService } from '../../../core/data-services/company';
import { TranslateService } from '@ngx-translate/core';
import { RmsColors } from '../../constants/rms-colors';
import { TradeBlotterOrder } from '../../models/trade-blotter-order.model';
import { TradeBlotterSide } from '../../enums/trade-blotter-side.enum';
import { TradeRestrictionLevel } from '../../enums/trade-restriction-level.enum';
import {BoardSeatService} from "../../../core/data-services/board-seat.service";
import {CONFIG} from "../../../config";
import {PaginatedList} from "../../../xform-compat";


const backgroundColor = '#FFFFFF';
const axisColor = '#929292';
const cutoffColor = '#9F3E52';
const labelColor = '#6C757D';
const portfolioDataPointColor = '#949494';
const portfolioKIDataPointColor = '#2563F5';
const portfolioEditingColor = '#003552';
const nonPortfolioDataPointColor = '#5D9E00';
const nonPortofolioKIDataPointColor = '#F4F2FD';
const nonPortfolioEditingColor = '#21908F';
const fontFamilyBook = '"Conv_Gotham-Book", "Helvetica Neue", sans-serif';
const fontFamilyMedium = '"Gotham-Medium", "Helvetica Neue", sans-serif';
const pointLabelRichStyles = {
  greenLabel: {
    color: RmsColors.RMS_PRIMARY_COLOR,
    fontSize: 8,
    fontFamily: fontFamilyBook,
  },
  maroonLabel: {
    color: cutoffColor,
    fontSize: 8,
    fontFamily: fontFamilyBook,
  },
  orangeLabel: {
    color: '#F79B41',
    fontSize: 8,
    fontFamily: fontFamilyBook,
  },
  defaultLabel: {
    color: labelColor,
    fontSize: 8,
    fontFamily: fontFamilyBook,
  },
};
const axisLabelRichStyles = {
  axisLabelBook: {
    fontSize: 10,
    fontFamily: fontFamilyBook,
  },
  axisLabelMedium: {
    fontSize: 10,
    fontFamily: fontFamilyMedium,
  },
};

@Component({
    selector: 'rms-portfolio-optix',
    templateUrl: './portfolio-optix.component.html',
    styleUrls: ['./portfolio-optix.component.scss'],
    standalone: false
})
export class PortfolioOptixComponent implements OnInit, OnChanges, OnDestroy {

  private static CUTOFF_LINE = 'CUTOFF_LINE';
  private static PORTFOLIO_DATA = 'PORTFOLIO_DATA';
  private static PORTFOLIO_DATA_KI = 'PORTFOLIO_DATA_KI';
  private static NON_PORTFOLIO_DATA = 'NON_PORTFOLIO_DATA';
  private static NON_PORTFOLIO_DATA_KI = 'NON_PORTFOLIO_DATA_KI';

  @Input() portfolioData: Array<Position>;
  @Input() tradeBlotterData: Map<TradeBlotterSide, Array<TradeBlotterOrder>> = new Map<TradeBlotterSide, Array<TradeBlotterOrder>>();
  @Input() showPortfolioOptix: Boolean;
  @Input() isExpanded: Boolean;
  @Input() companyRestrictionData: {'soft': Set<number>, 'hard': Set<number>} = {'soft': null, 'hard': null};
  @Input() expandPortfolioOptix: () => {};
  @Output() onSelectionChanged = new EventEmitter<Array<Position>>();

  public chartInstance: any;
  public updateOptions: any;
  public chartOption: any;
  public data: any;
  public currentCount = 0;

  private portfolioPlotData = [];
  private portfolioKIPlotData = [];
  private nonPortfolioPlotData = [];
  private nonPortfolioKIPlotData = [];

  public showBod = null;
  public showOwnership = null;
  public showKeyInflection = null;

  public restrictionFilter: Array<{label: string, style: string, level: TradeRestrictionLevel, state: boolean}> = Array.from([
    { label: 'Hard', style: 'maroon', level: TradeRestrictionLevel.HARD, state: true },
    { label: 'Soft', style: 'orange', level: TradeRestrictionLevel.SOFT, state: true }
  ]);

  public blotterFilter: Array<{label: string, style: string, side: TradeBlotterSide, state: boolean}> = Array.from([
    { label: 'Buy', style: 'green', side: TradeBlotterSide.BUY, state: true },
    { label: 'Sell', style: 'maroon', side: TradeBlotterSide.SELL, state: true },
    { label: 'Short', style: 'maroon', side: TradeBlotterSide.SHORT, state: true },
  ]);

  public selectedPortfolio = [];
  public selectedNonPortfolio = [];

  public editedPortfolio = [];
  public editedNonPortolio = [];

  public hasPermissions: { [key: string]: boolean } = {};
  public searchTerm: String;

  private companyIdsWithKeyInf = [];
  private companyKIMap: { [key: number]: string } = {};

  private showCuttOff = true;

  private companyBoardSeatSet: Set<number> = new Set();

  constructor(
    private authorizationService: AuthorizationService,
    private keyInflectionDataService: KeyInflectionDataService,
    private financialService: FinancialService,
    private cd: ChangeDetectorRef,
    private translate: TranslateService,
    private boardSeatService: BoardSeatService,
   ) {
    this.hasPermissions = {
      readMarketData: this.authorizationService.hasPermission([PERMISSIONS.ReadMarketData]),
      corePriceTargetRead: this.authorizationService.hasPermission([PERMISSIONS.CorePriceTargetRead]),
      coreCompanyFinancialRead: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyFinancialRead])
    };
    this.boardSeatService.getBoardSeats(
      {
        active: true,
        representing: 'RA_CAP',
        page: 0,
        pagesize: CONFIG.pagination.maxElasticSearchPageSize
      }).subscribe((data: PaginatedList<BoardSeat>) => {
        if (data) {
          data.records.map(boardSeat => boardSeat.company?.id).forEach(id => {
            if (id) {
              this.companyBoardSeatSet.add(id);
            }
          });
        }
    });
   }


  ngOnDestroy(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.portfolioData) {
      this.fetchMilestoneKeyInflectionData();
      this.calculateValues();
      this.separateKIData();

      // Only initialize chart options if this is the first time or if portfolioData was previously empty
      if (!this.chartOption || (changes.portfolioData.previousValue && changes.portfolioData.previousValue.length === 0)) {
        this.initializeChartOptions();
      } else {
        this.resetChart();
      }
    }

    if (changes.portfolioData && changes.portfolioData.previousValue && changes.portfolioData.previousValue.length === 0) {
      if (this.showCuttOff && this.chartInstance) {
        setTimeout(() => {
          this.resetChart();
        });
      }
    }

    if (changes.portfolioData && changes.portfolioData.currentValue && changes.portfolioData.currentValue.length > 0 && this.chartInstance) {
      this.chartInstance.hideLoading();
    }
  }

  private initializeChartOptions(): void {
    this.chartOption = {
      backgroundColor: backgroundColor,
      grid: {
        left: '78px',
        right: '21px',
        bottom: '62px',
      },
      xAxis: {
        name: `{axisLabelMedium|${this.translate.instant('PORTFOLIO_OPTIX.PROBABILITY_ADJUSTED_DOWNSIDE_AXIS')}}{axisLabelBook| (%)}`,
        position: 'bottom',
        nameLocation: 'center',
        nameTextStyle: {
          rich: axisLabelRichStyles,
        },
        nameGap: 30,
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
        axisTick: {
          lineStyle: {
            color: axisColor,
          },
        },
        axisLabel: {
          formatter: this.axisLabelFormatter.bind(this),
          rich: axisLabelRichStyles,
        },
      },
      yAxis: {
        name: `{axisLabelMedium|${this.translate.instant('PORTFOLIO_OPTIX.UPSIDE_AXIS')}}{axisLabelBook| (%)}`,
        position: 'left',
        nameLocation: 'center',
        nameTextStyle: {
          rich: axisLabelRichStyles,
        },
        nameGap: 49,
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
        axisTick: {
          lineStyle: {
            color: axisColor,
          },
        },
        axisLabel: {
          formatter: this.axisLabelFormatter.bind(this),
          rich: axisLabelRichStyles,
        },
      },
      brush: {
        toolbox: ['polygon'],
        transformable: false,
        outOfBrush: {
          color: backgroundColor
        }
      },
      toolbox: {
        right: 15,
        feature: {
          dataZoom: {}
        }
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
        },
        {
          type: 'inside',
          yAxisIndex: 0,
        }
      ],
      tooltip: {
        trigger: 'item',
        formatter: this.tooltipCallback.bind(this),
        enterable: true,
      },
      graphic: [
        {
          type: 'group',
          right: 110,
          top: 11,
          children: [
            {
              type: 'circle',
              shape: { cx: -30, cy: 0, r: 5 },
              style: {
                fill: portfolioDataPointColor,
                stroke: portfolioDataPointColor,
                lineWidth: 1
              },
              cursor: 'default'
            },
            {
              type: 'text',
              style: {
                text: 'Portfolio',
                x: -22,
                y: 0,
                textAlign: 'left',
                textVerticalAlign: 'middle',
                fontSize: 10,
                fontFamily: fontFamilyBook,
                fill: '#333'
              },
              cursor: 'default'
            },
            {
              type: 'polygon',
              shape: {
                points: [[40, -5], [45, 0], [40, 5], [35, 0]]
              },
              style: {
                fill: portfolioDataPointColor,
                stroke: portfolioDataPointColor,
                lineWidth: 1
              },
              cursor: 'default'
            },
            {
              type: 'text',
              style: {
                text: 'BOD/10% ownership',
                x: 48,
                y: 0,
                textAlign: 'left',
                textVerticalAlign: 'middle',
                fontSize: 10,
                fontFamily: fontFamilyBook,
                fill: '#333'
              },
              cursor: 'default'
            },
            {
              type: 'circle',
              shape: { cx: 168, cy: 0, r: 5 },
              style: {
                fill: portfolioKIDataPointColor,
                stroke: portfolioKIDataPointColor,
                lineWidth: 1
              },
              cursor: 'default'
            },
            {
              type: 'text',
              style: {
                text: '/',
                x: 176,
                y: 0,
                textAlign: 'center',
                textVerticalAlign: 'middle',
                fontSize: 10,
                fontFamily: fontFamilyBook,
                fill: '#333'
              },
              cursor: 'default'
            },
            {
              type: 'polygon',
              shape: {
                points: [[183, -5], [188, 0], [183, 5], [178, 0]]
              },
              style: {
                fill: portfolioKIDataPointColor,
                stroke: portfolioKIDataPointColor,
                lineWidth: 1
              },
              cursor: 'default'
            },
            {
              type: 'text',
              style: {
                text: 'KI < 1Yr',
                x: 191,
                y: 0,
                textAlign: 'left',
                textVerticalAlign: 'middle',
                fontSize: 10,
                fontFamily: fontFamilyBook,
                fill: '#333'
              },
              cursor: 'default'
            }
          ]
        }
      ],
      series: this.buildSeriesConfig()
    };
  }

  private buildSeriesConfig(): Array<any> {
    const portfolioDataSeries = this.getDataForSeries(PortfolioOptixComponent.PORTFOLIO_DATA).map(d => d.plottedValue);
    const portfolioDataSeriesFiltered = this.filterNanValues(portfolioDataSeries);
    const portfolioDataKiSeries = this.getDataForSeries(PortfolioOptixComponent.PORTFOLIO_DATA_KI).map(d => d.plottedValue);
    const portfolioDataKiSeriesFiltered = this.filterNanValues(portfolioDataKiSeries);
    const nonPortfolioDataSeries = this.getDataForSeries(PortfolioOptixComponent.NON_PORTFOLIO_DATA).map(d => d.plottedValue);
    const nonPortfolioDataSeriesFiltered = this.filterNanValues(nonPortfolioDataSeries);
    const nonPortfolioDataKiSeries = this.getDataForSeries(PortfolioOptixComponent.NON_PORTFOLIO_DATA_KI).map(d => d.plottedValue);
    const nonPortfolioDataKiSeriesFiltered = this.filterNanValues(nonPortfolioDataKiSeries);
    this.currentCount = portfolioDataSeriesFiltered.length + portfolioDataKiSeriesFiltered.length +
      nonPortfolioDataSeriesFiltered.length + nonPortfolioDataKiSeriesFiltered.length;
    return [
      (this.showCuttOff && this.portfolioData && this.portfolioData.length > 0 ? {
        id: PortfolioOptixComponent.CUTOFF_LINE,
        type: 'line',
        lineStyle: {
          type: 'dashed',
          color: cutoffColor
        },
        animation: false,
        symbol: 'none',
        silent: true,
        data: this.calculateCutoffLinePoints(),
      } : null),
      {
        id: PortfolioOptixComponent.PORTFOLIO_DATA,
        name: 'Portfolio',
        data: portfolioDataSeries,
        type: 'scatter',
        symbol: this.symbolFormatter.bind(this),
        symbolSize: this.symbolSizeFormatter.bind(this),
        animation: false,
        label: {
          show: true,
          position: 'bottom',
          formatter: this.pointFormatter.bind(this),
          rich: pointLabelRichStyles
        },
        itemStyle: {
          borderColor: portfolioDataPointColor,
          color: this.pointColorer.bind(this),
        },
        emphasis: {
          scale: true
        },
      },
      {
        id: PortfolioOptixComponent.PORTFOLIO_DATA_KI,
        name: 'Portfolio KI',
        data: portfolioDataKiSeries,
        type: 'scatter',
        symbol: this.symbolFormatter.bind(this),
        symbolSize: this.symbolSizeFormatter.bind(this),
        animation: false,
        label: {
          show: true,
          position: 'bottom',
          formatter: this.pointFormatter.bind(this),
          rich: pointLabelRichStyles
        },
        itemStyle: {
          borderColor: portfolioKIDataPointColor,
          color: this.pointColorer.bind(this),
        },
        emphasis: {
          scale: true
        }
      },
      {
        id: PortfolioOptixComponent.NON_PORTFOLIO_DATA,
        data: nonPortfolioDataSeries,
        type: 'scatter',
        symbol: this.symbolFormatter.bind(this),
        symbolSize: this.symbolSizeFormatter.bind(this),
        animation: false,
        label: {
          show: true,
          position: 'bottom',
          formatter: this.pointFormatter.bind(this),
          rich: pointLabelRichStyles
        },
        itemStyle: {
          borderColor: nonPortfolioDataPointColor,
          color: this.pointColorer.bind(this),
        },
        emphasis: {
          scale: true
        }
      },
      {
        id: PortfolioOptixComponent.NON_PORTFOLIO_DATA_KI,
        data: nonPortfolioDataKiSeries,
        type: 'scatter',
        symbol: this.symbolFormatter.bind(this),
        symbolSize: this.symbolSizeFormatter.bind(this),
        animation: false,
        label: {
          show: true,
          position: 'bottom',
          formatter: this.pointFormatter.bind(this),
          rich: pointLabelRichStyles
        },
        itemStyle: {
          borderColor: nonPortofolioKIDataPointColor,
          color: this.pointColorer.bind(this),
        },
        emphasis: {
          scale: true
        }
      }
    ].filter(series => series !== null);
  }

  private filterNanValues(array: Array<any>) {
    return array.filter(d => !Array.isArray(d) || !d.some(value => isNaN(value)));
  }

  ngOnInit() {}

  onChartInit(chartInstance) {
    this.chartInstance = chartInstance;

    if (this.portfolioData && this.portfolioData.length === 0) {
      this.chartInstance.showLoading();
    }

    this.chartInstance.on('click', (params) => {
      const { seriesId } = params;
      if (seriesId === PortfolioOptixComponent.PORTFOLIO_DATA || seriesId === PortfolioOptixComponent.PORTFOLIO_DATA_KI) {
        this.handleSelectionChanged([this.getDataItem(params)]);
      }
    });

    this.chartInstance.on('brushSelected', (params) => {
      const { batch } = params;
      const [ selected ] = batch;
      let newlySelected = [];
      selected.selected.forEach(type => {
        if (type.seriesId === PortfolioOptixComponent.PORTFOLIO_DATA || type.seriesId === PortfolioOptixComponent.PORTFOLIO_DATA_KI) {
          newlySelected = [
            ...newlySelected,
            ...type.dataIndex.map(idx => this.getDataItem({ seriesId: type.seriesId, dataIndex: idx })).filter(d => d)];
        }
      });
      this.handleSelectionChanged(newlySelected);
    });

    this.chartInstance.on('datazoom', () => {
      if (this.showCuttOff) {
        const options = this.chartInstance.getOption();
        this.chartInstance.setOption({series: options.series.map( s => {
          if (s.id === PortfolioOptixComponent.CUTOFF_LINE) {
            return {
              ...s,
              id: PortfolioOptixComponent.CUTOFF_LINE,
              data: this.calculateCutoffLinePoints()
            };
          }
          return s;
        })});
      }
    });
  }

  private calculateCutoffLinePoints () {
    if (this.chartInstance && this.chartInstance.getModel() && this.portfolioData) {
      const getDashedLineFunctionY = (x) => -1 * x;
      const xAxisExtent = this.chartInstance.getModel().getComponent('xAxis', 0).axis.scale.getExtent();
      const [ startXValue, endXValue ] = xAxisExtent;
      const edge = 1e-3;
      const step = (endXValue - startXValue) / 1000;
      const cuttoffPoints = [];
      for (let i = startXValue + edge; i < endXValue - edge; i += step) {
        cuttoffPoints.push([i, getDashedLineFunctionY(i + step)]);
      }
      return cuttoffPoints;
    }
    return null;
  }

  public clearSelectedPortfolio() {
    this.selectedPortfolio = [];
    this.resetChart();
    this.clearSelectionPolygon();
  }

  public clearSelectedNonPortfolio() {
    this.selectedNonPortfolio = [];
    this.nonPortfolioPlotData = [];
    this.nonPortfolioKIPlotData = [];
    this.resetChart();
    this.clearSelectionPolygon();
  }

  public clearOnePortfolio(item: Position) {
    this.selectedPortfolio = this.selectedPortfolio.filter(p => p.company.id !== item.company.id);
    this.resetChart();
  }

  public clearOneNonPortfolio(item: Position) {
    this.selectedNonPortfolio = this.selectedNonPortfolio.filter(p => p.company.id !== item.company.id);
    this.nonPortfolioPlotData = this.nonPortfolioPlotData.filter(p => p.company.id !== item.company.id);
    this.nonPortfolioKIPlotData = this.nonPortfolioKIPlotData.filter(p => p.company.id !== item.company.id);
    this.resetChart();
  }

  public handleValueChange(event: {item: Position, newValue: number, type: string, currentValues: {'fv': number, 'dfv': number, 'pos': number}}) {
    const { type, newValue, item, currentValues } = event;
    const seriesId = this.getSeriesIdForItem(item);
    const newData = this.getDataForSeries(seriesId).map((d, i) => {
      if (d.company.id === item.company.id) {
        if (type === 'DFV') {
          d.plottedValue[0] = this.calculateX(newValue, d.lastTrade, currentValues.pos);
        }
        if (type === 'FV') {
          d.plottedValue[1] = this.calculateY(newValue, d.lastTrade);
        }
        if (type === 'POS') {
          d.plottedValue[0] = this.calculateX(currentValues.dfv, d.lastTrade, newValue);
        }
      }
      return d;
    });

    this.chartInstance.setOption( {
      series: [
        {
          id: seriesId,
          data: newData.map(d => d.plottedValue),
        }
      ]
    });
    setTimeout(() => {
      this.chartInstance.setOption( {
        series: [
          (this.showCuttOff ? {
            id: PortfolioOptixComponent.CUTOFF_LINE,
            data: this.calculateCutoffLinePoints()
          } : null),
        ]
      });

    });
  }

  handleEditChange(event) {
    const {isPortfolio, ids} = event;
    if (isPortfolio) {
      this.editedPortfolio = ids;
    } else {
      this.editedNonPortolio = ids;
    }
    this.resetChart();
  }

  public isPortfolio(c: CompanyBasic): boolean {
    return this.portfolioData.some(pd => pd.company.id === c.id);
  }


  public handleAddCompany(company) {
    if (company) {
      this.financialService.getFinancial(company.id).subscribe(cf => {
        const downsideFairValue = cf.priceTarget && cf.priceTarget.downsideFair.value != null ? cf.priceTarget.downsideFair.value : NaN;
        const fairValue = cf.useCalculatedFairValue ?
          (cf.priceTarget && cf.priceTarget.calculatedFair && cf.priceTarget.calculatedFair.value ? cf.priceTarget.calculatedFair.value : NaN) :
          (cf.priceTarget && cf.priceTarget.fair && cf.priceTarget.fair.value ? cf.priceTarget.fair.value : NaN);
        const lastTrade = cf.openMarketPricing && cf.openMarketPricing.lastTrade ? cf.openMarketPricing.lastTrade : NaN;
        const probabilityOfSuccess = cf.probabilityOfSuccess && cf.probabilityOfSuccess.value ? cf.probabilityOfSuccess.value : NaN;
        const isKICompany =  this.isKICompany({ company });
        const nonPortfolio = {
          company,
          companyFinancial: this.flattenCompanyFinancial(cf),
          downsideFairValue,
          fairValue,
          lastTrade,
          probabilityOfSuccess,
          currencyCode: cf.currencyCode && cf.currencyCode.valueText,
          plottedValue: [
            this.calculateX(downsideFairValue, lastTrade, probabilityOfSuccess),
            this.calculateY(fairValue, lastTrade)
          ]
        };
        if (isKICompany) {
          this.nonPortfolioKIPlotData = [...this.nonPortfolioKIPlotData.filter(d => d.company.id !== company.id), nonPortfolio ];
        } else {
          this.nonPortfolioPlotData = [...this.nonPortfolioPlotData.filter(d => d.company.id !== company.id), nonPortfolio ];
        }
        this.selectedNonPortfolio = [...this.nonPortfolioKIPlotData, ...this.nonPortfolioPlotData].sort((a, b) =>
          a.company.ticker > b.company.ticker ? 1 : -1
        );
        this.resetChart();
      });
    }
  }

  public handleAddSelectedCompany(company) {
    if (company) {
      const seriesId = this.getSeriesIdForItem({company});
      const data = this.getSeriesData(seriesId);
      const dataIndex = data.findIndex(d => d.company.id === company.id);
      this.handleSelectionChanged([this.getDataItem({ seriesId, dataIndex })]);
    }
  }

  private handleSelectionChanged(dataItems) {
    dataItems.forEach(di => {
      if (this.selectedPortfolio.every(p => p.id !== di.id)) {
        this.selectedPortfolio.push(di);
      }
    });
    this.selectedPortfolio = this.selectedPortfolio.sort((a, b) =>
      a.company.ticker > b.company.ticker ? 1 : -1
    );
    this.onSelectionChanged.emit(this.selectedPortfolio);
    this.resetChart();
  }


  public resetChart() {
    if (this.chartInstance) {
      this.chartInstance.setOption({
        series: this.buildSeriesConfig()
      }, { replaceMerge: ['series'] });
    }
  }

  private clearSelectionPolygon() {
    this.chartInstance.dispatchAction({
      type: 'brush',
      command: 'clear',
      areas: [],
    });
  }

  private pointColorer(params) {
    const item = this.getDataItem(params);
    const companyId = item && item.company && item.company.id;
    if (this.editedPortfolio.includes(`${companyId}`)) {
      return portfolioEditingColor;
    }
    if (this.editedNonPortolio.includes(`${companyId}`)) {
      return nonPortfolioEditingColor;
    }

    if (params.seriesId === PortfolioOptixComponent.NON_PORTFOLIO_DATA) {
      return nonPortfolioDataPointColor;
    }
    if (params.seriesId === PortfolioOptixComponent.NON_PORTFOLIO_DATA_KI) {
      return nonPortofolioKIDataPointColor;
    }

    if (this.isKICompany(item)) {
      return portfolioKIDataPointColor;
    }
    return portfolioDataPointColor;
  }

  private symbolSizeFormatter(value, params) {
    const item = this.getDataItem(params);
    if (this.selectedPortfolio.map(p => p.company.id).includes(item.company.id)) {
      return 12;
    }
    return 6;
  }

  private symbolFormatter(value, params) {
    const item = this.getDataItem(params);
    if (item && this.isBODOwnershipCompany(item)) {
      return 'diamond';
    }
    return 'circle';
  }

  private getDataItem(params) {
    const data = this.getDataForSeries(params.seriesId);
    if (data) {
      return data[params.dataIndex];
    }
  }

  private axisLabelFormatter(params) {
    if (Number(params) === 0) {
      return `{axisLabelMedium|${String(params)}}`;
    } else {
      return `{axisLabelBook|${String(params)}}`;
    }
  }

  private pointFormatter(params) {
    const item = this.getDataItem(params) as Position;
    let text = '';

    if (item && item.company  && item.company.ticker) {
      text = item.company.ticker;
    } else if (item && item.company && item.company.name) {
      text = item.company.name;
    } else {
      return '';
    }

    text = this.tradeBlotterFormatter(text, item);
    return this.restrictionLevelFormatter(text, item);
  }

  private tradeBlotterFormatter(text: string, item: Position) {
    const companyId = item.company.id;
    for (const [k, v] of this.tradeBlotterData) {
      for (const tbo of v) {
        if (companyId === tbo.company?.id) {
          return k === TradeBlotterSide.BUY ? `{greenLabel|${text}}` : `{maroonLabel|${text}}`;
        }
      }
    }
    return `{defaultLabel|${text}}`;
  }

  private restrictionLevelFormatter(text: string, item: Position) {
    if (item && item.company) {
      if (this.companyRestrictionData.hard.has(item.company.id)) {
        return text + ' {defaultLabel|(}{maroonLabel|R}{defaultLabel|)}';
      } else if (this.companyRestrictionData.soft.has(item.company.id)) {
        return text + ' {defaultLabel|(}{orangeLabel|R}{defaultLabel|)}';
      }
    }
    return text;
  }

  private tooltipCallback(items) {
    return this.portfolioTooltip(items);
  }

  private portfolioTooltip(items): string {
    const item = this.getDataItem(items);
    if (!item) {
      return '';
    }
    const companyName = RmsUtilsService.escapeHtml(item.company.name);
    const companyId = RmsUtilsService.escapeHtml(item.company.id);
    const companyTicker = RmsUtilsService.escapeHtml(item.company.ticker);
    let tt = `<table><tr><td colspan="4">${companyName}</td></tr>`;
    tt += `<tr><td><a target="_blank" href='/company/${companyId}'>${companyTicker}${this.isBODCompany(item) ? ' (B)' : ''}</a></td></tr>`;
    if (item.intradaySummary) {
      tt += `<tr><td><b>OS:</b> ${item.intradaySummary.percentF}%F | ${item.intradaySummary.percentC}%C</td></tr>`;
    }
    tt += `<tr><td><b>WIWIIW:</b> ${item.companyFinancial.wiwiiw ?
        FinancialUtilsService.convertMoneyCurrency(item.companyFinancial.wiwiiw, item.currencyCode) : '-'}</td></tr>`;
    tt += `<tr><td><b>FV:</b> ${item.companyFinancial.useCalculatedFairValue ? `<span style="color: ${RmsColors.RMS_TEAL_COLOR}">` :
      '<span>'}${FinancialUtilsService.convertMoneyCurrency(item.fairValue, item.currencyCode, 2)}</span>
                  <b>DFV:</b> ${FinancialUtilsService.convertMoneyCurrency(item.downsideFairValue, item.currencyCode, 2)}
                  <b>Price:</b> ${FinancialUtilsService.convertMoneyCurrency(item.lastTrade, item.currencyCode, 2)}</td></tr>`;
    if (item.companyFinancial.marketCap) {
      const mc = item.companyFinancial.marketCap;
      tt += `<tr><td><b>Market Cap:</b> ${FinancialUtilsService.convertMoneyCurrency(mc, item.currencyCode, 1)}</td></tr>`;
    }
    if (this.companyKIMap[item.company.id]) {
      const keyInflection = RmsUtilsService.escapeHtml(this.companyKIMap[item.company.id]);
      tt += `<tr><td><b>KI:</b> ${keyInflection}</tr>`;
    }
    return tt += `</table>`;
  }

  private calculateValues() {
    this.data = this.portfolioData.map(p => {
        const downsideFairValue =  this.getDownsideFairValue(p);
        const fairValue = this.getFairValue(p);
        const lastTrade = this.getLastTrade(p);
        const probabilityOfSuccess = this.getProbabilityOfSuccess(p);
        return {
          ...p,
          downsideFairValue,
          fairValue,
          lastTrade,
          probabilityOfSuccess,
          plottedValue: [
            this.calculateX(downsideFairValue, lastTrade, probabilityOfSuccess),
            this.calculateY(fairValue, lastTrade)
          ]
      };
    });
  }

  private getDownsideFairValue(position: Position): number {
    const { companyFinancial } = position;
    return companyFinancial && companyFinancial.downsideFairValue != null ? companyFinancial.downsideFairValue : NaN;
  }

  private getFairValue(position: Position): number {
    const { companyFinancial } = position;
    return companyFinancial && companyFinancial.useCalculatedFairValue ?
      (companyFinancial && companyFinancial.calculatedFairPrice != null ? companyFinancial.calculatedFairPrice : NaN) :
      (companyFinancial && companyFinancial.fairPrice != null ? companyFinancial.fairPrice : NaN);
  }

  private getLastTrade(position: Position): number {
    const { price } = position;
    return price && price.lastTrade != null ? price.lastTrade : NaN;
  }

  private getProbabilityOfSuccess(position: Position): number {
    const { companyFinancial } = position;
    return companyFinancial && companyFinancial.probabilityOfSuccess != null ?  companyFinancial.probabilityOfSuccess : NaN;
  }

  private getSeriesIdForItem(item: {company: CompanyBasic} | Position): string {
    if (this.isPortfolio(item.company)) {
      return this.isKICompany(item) ? PortfolioOptixComponent.PORTFOLIO_DATA_KI : PortfolioOptixComponent.PORTFOLIO_DATA;
    } else {
      return this.isKICompany(item) ? PortfolioOptixComponent.NON_PORTFOLIO_DATA_KI : PortfolioOptixComponent.NON_PORTFOLIO_DATA;
    }
  }

  private getDataForSeries(seriesId: string): Array<any> {
    return this.getSeriesData(seriesId).filter(d => this.applyFilters(d));
  }

  private getSeriesData(seriesId: string): Array<any> {
    if (seriesId === PortfolioOptixComponent.PORTFOLIO_DATA) {
      return this.portfolioPlotData;
    }
    if (seriesId === PortfolioOptixComponent.PORTFOLIO_DATA_KI) {
      return this.portfolioKIPlotData;
    }
    if (seriesId === PortfolioOptixComponent.NON_PORTFOLIO_DATA) {
      return this.nonPortfolioPlotData;
    }
    if (seriesId === PortfolioOptixComponent.NON_PORTFOLIO_DATA_KI) {
      return this.nonPortfolioKIPlotData;
    }
  }

  private applyFilters(p: Position): boolean {
    return this.applyBodFilter(p) && this.applyOwnerhsipFilter(p) &&
      this.applyKiFilter(p) && this.applyBlotterFilter(p) &&
      this.applyRestrictionFilter(p);
  }

  public handleSetFilter(event) {
    if (event.target.id === 'bodYes') {
      this.showBod = this.showBod === true ? null : true;
    }
    if (event.target.id === 'bodNo') {
      this.showBod = this.showBod === false ? null : false;
    }
    if (event.target.id === 'ownershipGTE') {
      this.showOwnership = this.showOwnership === true ? null : true;
    }
    if (event.target.id === 'ownershipLT') {
      this.showOwnership = this.showOwnership === false ? null : false;
    }
    if (event.target.id === 'keyInflectionGTE') {
      this.showKeyInflection = this.showKeyInflection === false ? null : false;
    }
    if (event.target.id === 'keyInflectionLT') {
      this.showKeyInflection = this.showKeyInflection === true ? null : true;
    }
    this.resetChart();
  }

  private applyBodFilter(p: Position): boolean {
    if (this.showBod === null) {
      return true;
    }
    return this.isBODCompany(p) === this.showBod;
  }

  private applyOwnerhsipFilter(p: Position): boolean {
    if (this.showOwnership === null) {
      return true;
    }
    return this.isOwnershipCompany(p) === this.showOwnership;
  }

  private applyKiFilter(p: Position): boolean {
    if (this.showKeyInflection === null) {
      return true;
    }
    return this.isKICompany(p) === this.showKeyInflection;
  }

  private applyBlotterFilter(p: Position): boolean {
    const companyId = p.company.id;
    for (const filter of this.blotterFilter) {
      if (!filter.state && this.tradeBlotterData.get(filter.side).some(order => order.company.id === companyId)) {
        return false;
      }
    }
    return true;
  }

  private applyRestrictionFilter(p: Position): boolean {
    for (const filter of this.restrictionFilter) {
      if (!filter.state && filter.level === TradeRestrictionLevel.HARD && this.companyRestrictionData.hard.has(p.company.id)) {
        return false;
      } else if (!filter.state && filter.level === TradeRestrictionLevel.SOFT && this.companyRestrictionData.soft.has(p.company.id)) {
        return false;
      }
    }
    return true;
  }

  private separateKIData() {
    this.portfolioPlotData = this.data.filter(d => !this.isKICompany(d));
    this.portfolioKIPlotData = this.data.filter(d => this.isKICompany(d));
  }

  private isKICompany(portfolio: {company?: CompanyBasic}): boolean {
    if (this.companyIdsWithKeyInf && this.companyIdsWithKeyInf.length > 0) {
      return this.companyIdsWithKeyInf.indexOf(portfolio.company.id) > 0;
    }
    return false;
  }

  private isBODOwnershipCompany(portfolio: Position): boolean {
    return this.isBODCompany(portfolio) || this.isOwnershipCompany(portfolio);
  }

  private isBODCompany(portfolio: Position): boolean {
    return this.companyBoardSeatSet.has(portfolio?.company?.id);
  }

  private isOwnershipCompany(portfolio: Position): boolean {
    return (portfolio.intradaySummary && portfolio.intradaySummary.percentC >= 10);
  }

  private calculateX(downsideFairValue: number, lastTrade: number, probabilityOfSuccess: number): number {
    return (downsideFairValue / lastTrade - 1) * (1 - (probabilityOfSuccess / 100)) * 100;
  }

  private calculateY(fairValue: number, lastTrade: number): number {
    return (fairValue / lastTrade - 1) * 100;
  }

  private fetchMilestoneKeyInflectionData() {
    const pageSize = 1000;
    const page = 0;

    this.keyInflectionDataService.getKeyInflections(pageSize, page).subscribe(data => {
      const aYearFromNow = new Date();
      aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1);
      const lessThanOneYear = data.records.filter(d => new Date(d.timeFrame.begin) < aYearFromNow);
      this.companyIdsWithKeyInf = lessThanOneYear.map(lt => lt.primaryCompanyContentRoot.companyId);
      this.companyKIMap = this.companyIdsWithKeyInf.reduce((acc, id) => {
        const pos = data.records.find(r => r.primaryCompanyContentRoot.companyId === id);
        if (pos) {
          return { ...acc, [id]: pos.timeFrame.description };
        }
        return acc;
      }, {});
      this.cd.markForCheck();
    });
  }

  private flattenCompanyFinancial(cf) {
    return Object.keys(cf).reduce((acc, key) => {
        if (typeof cf[key] === 'object' && cf[key] != null && 'value' in cf[key]) {
          return { ...acc, [key]: cf[key].value };
        }
        if (typeof cf[key] === 'object' && cf[key] != null && 'valueText' in cf[key]) {
          return { ...acc, [key]: cf[key].valueText };
        }
        if (key === 'priceTarget') {
          return { ...acc, [key]: this.flattenCompanyFinancial(cf[key]) };
        }
        return { ...acc, [key]: cf[key] };
    }, {});
  }
}
